import { FC, ReactNode } from 'react'
import {
  AccountCircleOutlined,
  ArrowBack,
  ExitToApp,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LanguageOutlined,
  NotificationsOutlined,
  PersonOutlined,
  PhoneIphoneOutlined,
  PhoneOutlined,
  SettingsOutlined,
  SupervisorAccount
} from '@mui/icons-material'
import {
  alpha,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { AppVersion } from 'components/AppVersion'
import { useUsersProfile } from 'services/profile/profile.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import {
  useMasquarade,
  useMasquaradeToken,
  useMasquaradeUser
} from 'services/masquarade/masquarade.hooks'
import { useLanguage } from 'services/i18n/i18n.hooks'
import { unregisterPushNotifications } from 'services/pushNotifications'
import theme from 'styles/theme'

interface MenuRowProps {
  icon: ReactNode
  tint: string
  title: string
  description?: string
  isActive?: boolean
  isLast?: boolean
  onClick: () => void
}

const MenuRow: FC<MenuRowProps> = ({ icon, tint, title, description, isActive, isLast, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      textAlign: 'left',
      gap: '14px',
      padding: '16px',
      borderBottom: isLast ? 'none' : `1px solid ${theme.r4r.greyLight}`,
      backgroundColor: isActive ? alpha(theme.r4r.azure, 0.08) : 'transparent',
      ':hover': { backgroundColor: alpha(theme.r4r.azure, 0.06) }
    }}>
    <Box
      sx={{
        width: '40px',
        height: '40px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: alpha(tint, 0.14),
        color: tint
      }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy }}>
        {title}
      </Typography>
      {!!description && (
        <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
          {description}
        </Typography>
      )}
    </Box>
    <KeyboardArrowRight sx={{ color: theme.r4r.grey, fontSize: '20px' }} />
  </ButtonBase>
)

interface DesktopNavRowProps {
  icon: ReactNode
  title: string
  isActive?: boolean
  onClick: () => void
}

const DesktopNavRow: FC<DesktopNavRowProps> = ({ icon, title, isActive, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      textAlign: 'left',
      gap: '14px',
      pt: '16px',
      pb: '17px',
      px: '28px',
      borderBottom: `1px solid ${theme.r4r.greyLight}`,
      backgroundColor: isActive ? 'rgba(31, 31, 115, 0.04)' : 'transparent',
      color: isActive ? theme.r4r.navy : theme.r4r.ink,
      ':hover': { backgroundColor: 'rgba(31, 31, 115, 0.04)' }
    }}>
    <Box sx={{ display: 'flex', color: 'inherit', '& .MuiSvgIcon-root': { fontSize: '20px' } }}>
      {icon}
    </Box>
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: '14px',
        fontWeight: isActive ? 700 : 400,
        color: 'inherit',
        textAlign: 'left'
      }}>
      {title}
    </Typography>
    <KeyboardArrowRight
      sx={{ fontSize: '16px', color: isActive ? theme.r4r.navy : theme.r4r.grey }}
    />
  </ButtonBase>
)

const UserProfile: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { accessToken, clearUserData } = useAccessToken()
  const { isMasquarade, clearMasquaradeToken } = useMasquaradeToken()
  const { clearMasquaradeUser } = useMasquaradeUser()
  const { canUseMasquerade } = useMasquarade()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data = { firstName: '', lastName: '', email: '' }, userInitials } = useUsersProfile()
  const { language, setLanguage, AVAILABLE_LANGUAGES } = useLanguage()

  const menuItems = [
    {
      path: '/user/account',
      icon: <SettingsOutlined sx={{ fontSize: '20px' }} />,
      tint: theme.r4r.azure,
      title: formatMessage({ id: 'userProfile.account', defaultMessage: 'Zarządzanie kontem' }),
      description: formatMessage({
        id: 'userProfile.accountDesc',
        defaultMessage: 'Hasło, bezpieczeństwo, deaktywacja'
      })
    },
    {
      path: '/user/personal-data',
      icon: isDesktop ? (
        <PersonOutlined sx={{ fontSize: '20px' }} />
      ) : (
        <AccountCircleOutlined sx={{ fontSize: '20px' }} />
      ),
      tint: theme.r4r.violet,
      title: formatMessage({ id: 'userProfile.personalData', defaultMessage: 'Dane osobowe' }),
      description: formatMessage({
        id: 'userProfile.personalDataDesc',
        defaultMessage: 'Imię, nazwisko, PESEL, dokumenty'
      })
    },
    {
      path: '/user/details',
      icon: isDesktop ? (
        <PhoneOutlined sx={{ fontSize: '20px' }} />
      ) : (
        <PhoneIphoneOutlined sx={{ fontSize: '20px' }} />
      ),
      tint: theme.r4r.cyan,
      title: formatMessage({ id: 'userProfile.details', defaultMessage: 'Dane kontaktowe' }),
      description: formatMessage({
        id: 'userProfile.detailsDesc',
        defaultMessage: 'Telefon, email, adres korespondencyjny'
      })
    },
    {
      path: '/user/constens',
      icon: <NotificationsOutlined sx={{ fontSize: '20px' }} />,
      tint: theme.r4r.green,
      title: formatMessage({ id: 'userProfile.constens', defaultMessage: 'Zgody i powiadomienia' }),
      description: formatMessage({
        id: 'userProfile.constensDesc',
        defaultMessage: 'Push, email, SMS, zgody marketingowe'
      })
    },
    ...(canUseMasquerade
      ? [
          {
            path: '/user/masquarade',
            icon: <SupervisorAccount sx={{ fontSize: '20px' }} />,
            tint: theme.r4r.navy,
            title: formatMessage({
              id: 'masquaradeListItem.masquarade',
              defaultMessage: 'Maskarada'
            }),
            description: undefined as string | undefined
          }
        ]
      : [])
  ]

  const logout = async () => {
    if (accessToken) {
      await unregisterPushNotifications(accessToken)
    }
    clearUserData()
    clearMasquaradeToken()
    clearMasquaradeUser()
  }

  const gradientAvatar = (size: number, fontSize: number) => (
    <Avatar
      sx={{
        width: size,
        height: size,
        background: theme.r4r.gradient,
        color: '#fff',
        fontSize: `${fontSize}px`,
        fontWeight: 800
      }}>
      {userInitials}
    </Avatar>
  )

  if (isDesktop) {
    const avatar = gradientAvatar(64, 20)

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 'calc(100vh - 56px)'
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '28px',
            py: '24px'
          }}>
          <IconButton
            onClick={() =>
              navigate(pathname.startsWith('/user/bank-account') ? '/user/details' : '/')
            }
            sx={{
              width: '32px',
              height: '32px',
              p: 0,
              borderRadius: 0,
              color: theme.r4r.ink
            }}>
            <ArrowBack sx={{ fontSize: '22px' }} />
          </IconButton>
          <Typography
            sx={{ fontSize: '20px', fontWeight: 700, color: theme.r4r.ink, lineHeight: '24px' }}>
            {pathname.startsWith('/user/bank-account')
              ? formatMessage({
                  id: 'userDetailsBankAccount.label',
                  defaultMessage: 'Dane bankowe'
                })
              : pathname.startsWith('/user/details')
                ? formatMessage({ id: 'userDetails.label', defaultMessage: 'Dane kontaktowe' })
                : pathname.startsWith('/user/masquarade')
                  ? formatMessage({
                      id: 'masquaradeListItem.masquarade',
                      defaultMessage: 'Maskarada'
                    })
                  : formatMessage({
                      id: 'userAccount.label',
                      defaultMessage: 'Zarządzanie kontem'
                    })}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            px: '28px',
            pb: '25px',
            borderBottom: `1px solid ${theme.r4r.greyLight}`
          }}>
          {isMasquarade ? (
            <Badge
              overlap='circular'
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Tooltip
                  title={formatMessage({
                    id: 'userProfile.supervisorAccount',
                    defaultMessage: 'Maskarada'
                  })}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: theme.r4r.yellow }}>
                    <SupervisorAccount sx={{ fontSize: '14px', color: theme.r4r.navy }} />
                  </Avatar>
                </Tooltip>
              }>
              {avatar}
            </Badge>
          ) : (
            avatar
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: '16px', fontWeight: 700, color: theme.r4r.ink, lineHeight: 1.25 }}>
              {data.firstName} {data.lastName}
            </Typography>
            {!!data.email && (
              <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
                {data.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          {menuItems.map((item) => (
            <DesktopNavRow
              key={item.path}
              icon={item.icon}
              title={item.title}
              isActive={
                item.path === '/user/details'
                  ? pathname.startsWith('/user/details') || pathname.startsWith('/user/bank-account')
                  : pathname.startsWith(item.path)
              }
              onClick={() => navigate(item.path)}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            px: '28px',
            pt: '17px',
            pb: '16px',
            borderTop: `1px solid ${theme.r4r.greyLight}`
          }}>
          <ButtonBase
            onClick={logout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: theme.r4r.red,
              fontFamily: muiTheme.typography.fontFamily,
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              lineHeight: '13px'
            }}>
            <ExitToApp sx={{ fontSize: '18px' }} />
            {formatMessage({ id: 'userProfile.logoutShort', defaultMessage: 'Wyloguj' })}
          </ButtonBase>
          <Select
            variant='standard'
            disableUnderline
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            IconComponent={(props) => (
              <KeyboardArrowDown {...props} sx={{ fontSize: '14px', color: theme.r4r.ink }} />
            )}
            sx={{
              fontSize: '13px',
              color: theme.r4r.ink,
              border: `1px solid ${theme.r4r.greyLight}`,
              backgroundColor: '#fff',
              px: '15px',
              py: '7px',
              '.MuiSelect-select': {
                padding: '0 !important',
                paddingRight: '20px !important'
              },
              '& .MuiSelect-icon': {
                right: '8px'
              }
            }}>
            {AVAILABLE_LANGUAGES.map(({ value, label }) => (
              <MenuItem key={label} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    )
  }

  const avatar = (
    <Avatar
      sx={{
        width: 80,
        height: 80,
        backgroundColor: alpha('#fff', 0.2),
        border: `2px solid ${alpha('#fff', 0.4)}`,
        color: '#fff',
        fontSize: '26px',
        fontWeight: 700
      }}>
      {userInitials}
    </Avatar>
  )

  return (
    <Box sx={{ pt: 'env(safe-area-inset-top)' }}>
      <Box
        sx={{
          background: theme.r4r.gradient,
          boxShadow: theme.r4r.cardShadow,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '36px 24px 28px'
        }}>
        {isMasquarade ? (
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Tooltip
                title={formatMessage({
                  id: 'userProfile.supervisorAccount',
                  defaultMessage: 'Maskarada'
                })}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.r4r.yellow }}>
                  <SupervisorAccount sx={{ fontSize: '18px', color: theme.r4r.navy }} />
                </Avatar>
              </Tooltip>
            }>
            {avatar}
          </Badge>
        ) : (
          avatar
        )}
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff', mt: '14px' }}>
          {data.firstName} {data.lastName}
        </Typography>
        {!!data.email && (
          <Typography sx={{ fontSize: '13px', color: alpha('#fff', 0.75), mt: '4px' }}>
            {data.email}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: '16px' }}>
        <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow, mt: '16px' }}>
          {menuItems.map((item, index) => (
            <MenuRow
              key={item.path}
              icon={item.icon}
              tint={item.tint}
              title={item.title}
              description={item.description}
              isLast={index === menuItems.length - 1}
              onClick={() => navigate(item.path)}
            />
          ))}
        </Box>

        <Box
          sx={{
            backgroundColor: '#fff',
            boxShadow: theme.r4r.cardShadow,
            mt: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '14px 16px'
          }}>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(theme.r4r.navy, 0.08),
              color: theme.r4r.navy
            }}>
            <LanguageOutlined sx={{ fontSize: '20px' }} />
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy, flex: 1 }}>
            {formatMessage({ id: 'userProfile.language', defaultMessage: 'Język' })}
          </Typography>
          <Select
            variant='standard'
            disableUnderline
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            sx={{
              fontSize: '13px',
              color: theme.r4r.grey,
              '.MuiSelect-select': { paddingRight: '24px !important' }
            }}>
            {AVAILABLE_LANGUAGES.map(({ value, label }) => (
              <MenuItem key={label} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <ButtonBase
          onClick={logout}
          sx={{
            width: '100%',
            mt: '16px',
            backgroundColor: '#fff',
            boxShadow: theme.r4r.cardShadow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            padding: '14px',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: muiTheme.typography.fontFamily,
            color: theme.r4r.red,
            ':hover': { backgroundColor: alpha(theme.r4r.red, 0.04) }
          }}>
          <ExitToApp sx={{ fontSize: '20px' }} />
          {formatMessage({ id: 'userProfile.logout', defaultMessage: 'Wyloguj się' })}
        </ButtonBase>

        <Typography
          onClick={() => navigate('/user/account')}
          sx={{
            fontSize: '13px',
            color: theme.r4r.grey,
            textDecoration: 'underline',
            textAlign: 'center',
            mt: '18px',
            cursor: 'pointer'
          }}>
          {formatMessage({ id: 'userProfile.deleteAccountLink', defaultMessage: 'Chcę usunąć konto' })}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '8px' }}>
          <AppVersion />
        </Box>
      </Box>
    </Box>
  )
}

export default UserProfile
